import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, FileText, Flag, Loader2, MonitorPlay, MonitorSmartphone, Settings2, Trash2 } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePermissions } from '@/hooks/usePermissions';
import * as repo from '../data/votingRepository';
import { useElectionState, useVotingMutation } from '../hooks/useVoting';
import { CandidatesPanel } from '../components/CandidatesPanel';
import { ElectionFormDialog, type ElectionFormValues } from '../components/ElectionFormDialog';
import { PhoneVotingPanel } from '../components/PhoneVotingPanel';
import { PositionsPanel } from '../components/PositionsPanel';
import { StatsPanel } from '../components/StatsPanel';
import { VotersPanel } from '../components/VotersPanel';
import { ATTENDANCE_LABEL, KINDS_LABEL, STATUS_LABEL } from '../utils/labels';
import { votingErrorMessage } from '../utils/votingErrors';

export function ElectionDetailPage() {
  const { electionId } = useParams<{ electionId: string }>();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { data, isLoading, error } = useElectionState(electionId);
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState<'finish' | 'delete' | null>(null);

  const update = useVotingMutation(electionId, (patch: Partial<ElectionFormValues> & { status?: string }) =>
    repo.updateElection(electionId as string, patch as never), { success: 'Guardado' });
  const remove = useVotingMutation(electionId, () => repo.deleteElection(electionId as string), { success: 'Elección eliminada' });

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>;
  if (error || !data) {
    return (
      <div className="space-y-3 p-6">
        <Link to="/voting" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="size-4" /> Elecciones</Link>
        <p className="text-sm text-destructive">{error ? votingErrorMessage(error) : 'No se encontró la elección.'}</p>
      </div>
    );
  }

  const { state, fetchedAt } = data;
  const e = state.election;
  const hasOpenRound = state.races.some((r) => r.status === 'OPEN');
  const canEdit = can('election.edit');
  const finished = e.status === 'CLOSED' || e.status === 'ARCHIVED';
  const hasRounds = state.races.length > 0;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <Link to="/voting" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="size-4" /> Elecciones</Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">{e.name}</h1>
            <Badge variant={e.status === 'ACTIVE' ? 'default' : 'secondary'}>{STATUS_LABEL[e.status as keyof typeof STATUS_LABEL]}</Badge>
          </div>
          {e.description && <p className="text-sm text-muted-foreground">{e.description}</p>}
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><CalendarDays className="size-4" /> {e.event_name ?? 'Sin evento (lista manual)'}</span>
            {e.event_id && (
              <span>
                Votan {KINDS_LABEL[e.voter_kinds as keyof typeof KINDS_LABEL]} · {ATTENDANCE_LABEL[e.attendance_rule as keyof typeof ATTENDANCE_LABEL]}
                {e.session_name ? ` (${e.session_name})` : ''}
              </span>
            )}
            <span>{Number(e.threshold_pct)} % para ganar</span>
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link to={`/proyeccion/${e.id}`} target="_blank"><MonitorPlay className="mr-2 size-4" /> Pantalla de proyección</Link>
          </Button>
          {hasRounds && (
            <Button variant="outline" asChild>
              <Link to={`/acta/${e.id}`} target="_blank"><FileText className="mr-2 size-4" /> Acta</Link>
            </Button>
          )}
          {can('vote.proxy') && e.status !== 'CLOSED' && e.status !== 'ARCHIVED' && (
            <Button variant="outline" asChild>
              <Link to={`/votar/${e.id}`} target="_blank"><MonitorSmartphone className="mr-2 size-4" /> Abrir kiosco</Link>
            </Button>
          )}
          {canEdit && !finished && (
            <Button variant="outline" onClick={() => setEditing(true)}><Settings2 className="mr-2 size-4" /> Configuración</Button>
          )}
          {canEdit && e.status === 'ACTIVE' && (
            <Button variant="outline" disabled={hasOpenRound} title={hasOpenRound ? 'Cierra la ronda abierta primero' : undefined} onClick={() => setConfirm('finish')}>
              <Flag className="mr-2 size-4" /> Finalizar elección
            </Button>
          )}
          {can('election.delete') && !hasRounds && (
            <Button variant="ghost" title="Eliminar elección" onClick={() => setConfirm('delete')}><Trash2 className="size-4 text-destructive" /></Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="rounds">
        <TabsList>
          <TabsTrigger value="rounds">Cargos y rondas</TabsTrigger>
          <TabsTrigger value="candidates">Candidatos ({state.candidates.length})</TabsTrigger>
          {!e.event_id && <TabsTrigger value="voters">Votantes</TabsTrigger>}
          {!finished && <TabsTrigger value="phone">Teléfono (QR)</TabsTrigger>}
          {can('vote.view') && hasRounds && <TabsTrigger value="stats">Estadísticas</TabsTrigger>}
        </TabsList>
        <TabsContent value="rounds" className="mt-4"><PositionsPanel state={state} fetchedAt={fetchedAt} /></TabsContent>
        <TabsContent value="candidates" className="mt-4"><CandidatesPanel state={state} canEdit={can('election_candidate.create') && !finished} /></TabsContent>
        {can('vote.view') && hasRounds && (
          <TabsContent value="stats" className="mt-4"><StatsPanel electionId={e.id} electionName={e.name} /></TabsContent>
        )}
        {!finished && (
          <TabsContent value="phone" className="mt-4"><PhoneVotingPanel electionId={e.id} eventId={e.event_id} /></TabsContent>
        )}
        {!e.event_id && (
          <TabsContent value="voters" className="mt-4"><VotersPanel electionId={e.id} canEdit={canEdit && !finished} /></TabsContent>
        )}
      </Tabs>

      <ElectionFormDialog
        open={editing}
        onOpenChange={setEditing}
        election={e}
        busy={update.isPending}
        onSubmit={(v) => update.mutate(v, { onSuccess: () => setEditing(false) })}
      />

      <AlertDialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === 'finish' ? '¿Finalizar la elección?' : '¿Eliminar la elección?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === 'finish'
                ? 'Ya no se podrán crear ni abrir rondas. Los resultados quedan guardados.'
                : 'Se borran sus cargos, candidatos y votantes. No se puede deshacer.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirm === 'finish') update.mutate({ status: 'CLOSED' });
                else remove.mutate(undefined, { onSuccess: () => navigate('/voting') });
                setConfirm(null);
              }}
            >
              Continuar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
